import { Controller } from "@hotwired/stimulus";
import { messageFor } from "../lib/auth_signup_validation";

const STRIPE_SCRIPT_URL = "https://js.stripe.com/v3/";
const CHECK_ICON_SRC = "/images/auth/check-icon.svg";

export default class extends Controller {
  static targets = [
    "planInput",
    "planCard",
    "planCheck",
    "cardToken",
    "submitButton",
    "cardNumberMount",
    "cardExpiryMount",
    "cardCvcMount",
    "zipError",
    "cardError",
    "stripeError",
    "captchaMount",
    "captchaError",
    "captchaToken",
    "fieldInput",
    "fieldError",
    "couponId",
    "promoInput",
    "promoError",
    "promoApplyWrap",
    "promoInputWrap",
    "promoAppliedWrap",
    "promoBadge",
    "promoDiscount",
    "totalDue",
  ];

  static values = {
    stripeKey: String,
    showCaptcha: Boolean,
    captchaSiteKey: String,
    showPromo: Boolean,
    couponUrl: String,
    baseAmount: Number,
    checkIcon: { type: String, default: CHECK_ICON_SRC },
  };

  connect() {
    this.selectedPlanId = this.planInputTarget?.value || "";
    this.stripeReady = false;
    this.captchaWidgetId = null;
    this.submitting = false;
    this.busy = false;
    this.promoDiscountAmount = 0;
    this.promoApplied = false;

    this.updatePlanVisuals();
    this.updateSubmitState();
    this.initStripe();
    this.onCaptchaPageReady = () => this.scheduleCaptchaInit();
    document.addEventListener("auth-loader:ready", this.onCaptchaPageReady);
    if (document.body.classList.contains("auth-page-ready")) {
      this.scheduleCaptchaInit();
    }
  }

  scheduleCaptchaInit() {
    if (this.captchaScheduled || !this.captchaRequired()) return;
    this.captchaScheduled = true;
    window.requestAnimationFrame(() => this.initCaptcha());
  }

  showLoader() {
    document.body.classList.remove("auth-page-ready");
    document.body.classList.add("auth-submitting");
    const loader = document.querySelector(".auth-loader");
    if (loader) loader.classList.remove("auth-loader--hidden");
    document.dispatchEvent(new CustomEvent("auth-loader:show"));
  }

  hideLoader() {
    document.dispatchEvent(new CustomEvent("auth-loader:ready"))
    document.dispatchEvent(new CustomEvent("auth-loader:hide"))
  }

  disconnect() {
    document.removeEventListener("auth-loader:ready", this.onCaptchaPageReady);
    if (this.captchaRetryTimer) window.clearTimeout(this.captchaRetryTimer);
    this.unmountStripeElements();
  }

  get publishableKey() {
    const fromValue = this.hasStripeKeyValue ? this.stripeKeyValue.trim() : "";
    if (fromValue) return fromValue;

    return (
      document.querySelector('meta[name="stripe-key"]')?.content?.trim() || ""
    );
  }

  async initStripe() {
    const key = this.publishableKey;
    if (!key) {
      this.showStripeError(
        "Payment form is unavailable. Missing Stripe publishable key.",
      );
      this.hideLoader();
      return;
    }

    try {
      await this.ensureStripeScript();
      this.setupStripe(key);
    } catch (_error) {
      this.showStripeError(
        "Unable to load payment form. Please refresh and try again.",
      );
    } finally {
      if (!this.submitting) this.hideLoader();
    }
  }

  ensureStripeScript() {
    if (window.Stripe) return Promise.resolve();

    const existing = document.querySelector(
      `script[src="${STRIPE_SCRIPT_URL}"]`,
    );
    if (existing) {
      return new Promise((resolve, reject) => {
        if (window.Stripe) return resolve();
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(), { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = STRIPE_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Stripe script failed"));
      document.head.appendChild(script);
    });
  }

  setupStripe(key) {
    if (this.stripeReady) return;

    this.stripe = window.Stripe(key);
    const elements = this.stripe.elements();
    const style = {
      base: {
        fontSize: "16px",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        fontWeight: "300",
        color: "#2a2626",
        "::placeholder": { color: "#6B7280" },
      },
      invalid: { color: "#9e2146" },
    };

    this.cardNumber = elements.create("cardNumber", { style, placeholder: "" });
    this.cardExpiry = elements.create("cardExpiry", {
      style,
      placeholder: "MM/YY",
    });
    this.cardCvc = elements.create("cardCvc", { style, placeholder: "CVC" });

    const onChange = (event) => {
      if (event.error) {
        this.showCardError(event.error.message);
      } else if (this.hasCardErrorTarget) {
        this.cardErrorTarget.textContent = "";
      }
    };

    this.cardNumber.on("change", onChange);
    this.cardExpiry.on("change", onChange);
    this.cardCvc.on("change", onChange);

    if (this.hasCardNumberMountTarget)
      this.cardNumber.mount(this.cardNumberMountTarget);
    if (this.hasCardExpiryMountTarget)
      this.cardExpiry.mount(this.cardExpiryMountTarget);
    if (this.hasCardCvcMountTarget) this.cardCvc.mount(this.cardCvcMountTarget);

    this.stripeReady = true;
    if (this.hasStripeErrorTarget) this.stripeErrorTarget.textContent = "";
    this.updateSubmitState();
  }

  unmountStripeElements() {
    [this.cardNumber, this.cardExpiry, this.cardCvc].forEach((element) => {
      if (element) {
        try {
          element.unmount();
        } catch (_error) {
          // already unmounted
        }
      }
    });
    this.stripeReady = false;
  }

  selectPlan(event) {
    event.preventDefault();
    const card = event.currentTarget;
    this.selectedPlanId = card.dataset.planId;
    if (this.hasPlanInputTarget)
      this.planInputTarget.value = this.selectedPlanId;
    this.updatePlanVisuals();
    this.updateSubmitState();
  }

  selectPlanKeydown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.selectPlan(event);
    }
  }

  updatePlanVisuals() {
    this.planCardTargets.forEach((card) => {
      const selected = card.dataset.planId === this.selectedPlanId;
      card.classList.toggle("auth-plan-card--selected", selected);
      card.classList.toggle("border-coffee", selected);
      card.classList.toggle("border-gray", !selected);
      card.setAttribute("aria-pressed", selected ? "true" : "false");
    });

    if (this.hasPlanCheckTarget) {
      this.planCheckTargets.forEach((check) => {
        const card = check.closest("[data-signup-target~='planCard']");
        const selected = card?.dataset.planId === this.selectedPlanId;
        check.classList.toggle("hidden", !selected);
      });
    }
  }

  initCaptcha() {
    if (!this.captchaRequired() || this.captchaWidgetId !== null) return;
    if (this.hasCaptchaMountTarget && this.captchaMountTarget.childElementCount > 0) {
      return;
    }

    const render = () => {
      if (
        this.captchaWidgetId !== null ||
        !this.hasCaptchaMountTarget ||
        this.captchaMountTarget.childElementCount > 0
      ) {
        return;
      }

      try {
        this.captchaWidgetId = window.grecaptcha.render(
          this.captchaMountTarget,
          {
            sitekey: this.captchaSiteKeyValue,
          },
        );
      } catch (_error) {
        this.notifyError(
          "Unable to load captcha. Please refresh the page and try again.",
        );
      }
    };

    if (window.grecaptcha?.ready) {
      window.grecaptcha.ready(render);
      return;
    }

    if (window.grecaptcha?.render) {
      render();
      return;
    }

    this.captchaRetryTimer = window.setTimeout(() => this.initCaptcha(), 150);
  }

  captchaRequired() {
    return (
      this.showCaptchaValue &&
      this.hasCaptchaMountTarget &&
      !!this.captchaSiteKeyValue
    );
  }

  validateFieldInput(event) {
    const input = event.currentTarget
    const field = input.dataset.signupField
    if (!field) return

    this.runFieldValidation(field, input)
  }

  validateFields() {
    let valid = true

    this.fieldInputTargets.forEach((input) => {
      const field = input.dataset.signupField
      if (!field) return
      if (!this.runFieldValidation(field, input)) valid = false
    })

    return valid
  }

  runFieldValidation(field, input) {
    const password = this.passwordValue()
    const confirm = this.confirmPasswordValue()
    const message = messageFor(field, input, password, confirm)

    this.setFieldError(field, message)
    input.setAttribute("aria-invalid", message ? "true" : "false")
    this.clearServerFieldError(field)

    if (field === "password" && this.confirmPasswordInput()) {
      const confirmInput = this.confirmPasswordInput()
      const confirmMessage = messageFor(
        "password_confirmation",
        confirmInput,
        password,
        confirm,
      )
      this.setFieldError("password_confirmation", confirmMessage)
      confirmInput.setAttribute("aria-invalid", confirmMessage ? "true" : "false")
    }

    return !message
  }

  passwordValue() {
    return this.passwordInput()?.value || ""
  }

  confirmPasswordValue() {
    return this.confirmPasswordInput()?.value || ""
  }

  passwordInput() {
    return this.fieldInputTargets.find(
      (el) => el.dataset.signupField === "password",
    )
  }

  confirmPasswordInput() {
    return this.fieldInputTargets.find(
      (el) => el.dataset.signupField === "password_confirmation",
    )
  }

  clearServerFieldError(field) {
    this.element
      .querySelectorAll(`[data-signup-server-error="${field}"]`)
      .forEach((el) => el.remove())
  }

  setFieldError(field, message) {
    const errorEl = this.fieldErrorTargets.find(
      (el) => el.dataset.signupField === field,
    )
    if (errorEl) {
      errorEl.textContent = message
      errorEl.hidden = !message
    }
    return !message
  }

  async beforeSubmit(event) {
    if (this.submitting) return;

    event.preventDefault();

    if (this.busy) return;

    if (!this.validateFields()) {
      this.notifyError("Please fix the errors below and try again.");
      this.scrollToFirstError();
      return;
    }

    if (!this.selectedPlanId) {
      this.notifyError("Please select a plan to continue.");
      return;
    }

    if (this.captchaRequired()) {
      if (this.captchaWidgetId === null) {
        this.scheduleCaptchaInit();
        this.notifyError("Captcha is still loading. Please wait a moment.");
        return;
      }

      const captchaResponse = window.grecaptcha?.getResponse(
        this.captchaWidgetId,
      );
      if (!captchaResponse) {
        if (this.hasCaptchaErrorTarget) {
          this.captchaErrorTarget.textContent = "Please solve captcha";
        }
        this.notifyError("Please solve captcha");
        return;
      }
      if (this.hasCaptchaTokenTarget)
        this.captchaTokenTarget.value = captchaResponse;
    }

    if (!this.stripeReady || !this.cardNumber) {
      this.showStripeError(
        "Payment form is still loading. Please wait a moment and try again.",
      );
      this.notifyError(
        "Payment form is still loading. Please wait a moment and try again.",
      );
      return;
    }

    const zip = this.element.querySelector("[name='user[zip_code]']")?.value?.trim();

    if (!zip) {
      this.showZipError("Zip code required.");
      this.notifyError("Zip code required.");
      return;
    }

    this.showZipError("");
    this.showCardError("");

    this.busy = true;
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;

    try {
      const { token, error } = await this.stripe.createToken(this.cardNumber, {
        address_zip: zip,
      });

      if (error) {
        this.showCardError(error.message);
        this.notifyError(error.message);
        return;
      }

      if (token.card?.funding === "prepaid") {
        const message =
          "We do not accept prepaid cards. Please use a credit or debit card to continue";
        this.showCardError(message);
        this.notifyError(message);
        return;
      }

      this.cardTokenTarget.value = token.id;
      this.submitting = true;
      this.showLoader();
      // Second pass allows the form's native navigation to render the success flash once.
      this.element.requestSubmit();
    } finally {
      this.busy = false;
      if (!this.submitting && this.hasSubmitButtonTarget) {
        this.updateSubmitState();
      }
    }
  }

  showZipError(message) {
    if (this.hasZipErrorTarget) this.zipErrorTarget.textContent = message;
  }

  showCardError(message) {
    if (this.hasCardErrorTarget) this.cardErrorTarget.textContent = message;
  }

  showStripeError(message) {
    if (this.hasStripeErrorTarget) {
      this.stripeErrorTarget.textContent = message;
    } else {
      this.showCardError(message);
    }
  }

  updateSubmitState() {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled =
      this.submitting ||
      this.busy ||
      !this.selectedPlanId ||
      !this.publishableKey ||
      !this.stripeReady;
  }

  notifyError(message) {
    if (!message) return;
    document.dispatchEvent(
      new CustomEvent("auth:toast", {
        detail: { message, messageType: "error" },
      }),
    );
  }

  scrollToFirstError() {
    const first =
      this.fieldErrorTargets.find((el) => el.textContent.trim()) ||
      this.element.querySelector(".auth-error:not(:empty)");

    first?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  onPromoInput() {
    if (!this.showPromoValue || this.promoApplied) return;

    const code = this.promoInputTarget?.value?.trim() || "";
    this.hidePromoError();
    this.setPromoApplyVisible(code.length > 0);
  }

  async applyPromo(event) {
    event.preventDefault();
    if (!this.showPromoValue || !this.hasPromoInputTarget) return;

    const code = this.promoInputTarget.value.trim();
    if (!code) return;

    const url = `${this.couponUrlValue}?code=${encodeURIComponent(code)}`;

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
      });
      const payload = await response.json();
      const data = payload?.data || {};

      if (data.valid) {
        this.promoApplied = true;
        this.promoDiscountAmount = parseInt(data.coupon_discount, 10) || 0;
        if (this.hasCouponIdTarget) {
          this.couponIdTarget.value = data.coupon_id || "";
        }
        this.showAppliedPromo(data.coupon_code, this.promoDiscountAmount);
        this.updateTotalDue();
        this.hidePromoError();
      } else {
        this.showPromoError("This code is invalid.");
      }
    } catch (_error) {
      this.showPromoError("Unable to validate code. Please try again.");
    }
  }

  clearPromo(event) {
    event.preventDefault();
    this.promoApplied = false;
    this.promoDiscountAmount = 0;
    if (this.hasCouponIdTarget) this.couponIdTarget.value = "";
    if (this.hasPromoInputTarget) this.promoInputTarget.value = "";

    if (this.hasPromoInputWrapTarget) {
      this.promoInputWrapTarget.classList.remove("hidden");
    }
    if (this.hasPromoAppliedWrapTarget) {
      this.promoAppliedWrapTarget.classList.add("hidden");
    }
    this.setPromoApplyVisible(false);
    this.hidePromoError();
    this.updateTotalDue();
  }

  showAppliedPromo(code, discount) {
    if (this.hasPromoInputWrapTarget) {
      this.promoInputWrapTarget.classList.add("hidden");
    }
    this.setPromoApplyVisible(false);
    if (this.hasPromoAppliedWrapTarget) {
      this.promoAppliedWrapTarget.classList.remove("hidden");
    }
    if (this.hasPromoBadgeTarget) this.promoBadgeTarget.textContent = code;
    if (this.hasPromoDiscountTarget) {
      this.promoDiscountTarget.textContent = `$${discount} off`;
    }
  }

  setPromoApplyVisible(visible) {
    if (!this.hasPromoApplyWrapTarget) return;
    this.promoApplyWrapTarget.classList.toggle("hidden", !visible);
  }

  showPromoError(message) {
    if (!this.hasPromoErrorTarget) return;
    this.promoErrorTarget.textContent = message;
    this.promoErrorTarget.hidden = false;
  }

  hidePromoError() {
    if (!this.hasPromoErrorTarget) return;
    this.promoErrorTarget.textContent = "";
    this.promoErrorTarget.hidden = true;
  }

  updateTotalDue() {
    if (!this.hasTotalDueTarget) return;
    const base = this.baseAmountValue || 0;
    const due = Math.max(base - this.promoDiscountAmount, 0);
    this.totalDueTarget.textContent = `Total Due Today: $${due}`;
  }
}
